import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft, Send, Hash, Megaphone, User } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { format } from 'date-fns';

const MOCK_ROOMS = {
  'room-engineering': { name: 'Engineering', type: 'department', members: 8 },
  'room-general':     { name: 'General',     type: 'department', members: 24 },
  'room-hr':          { name: 'HR',          type: 'department', members: 5 },
  'room-announce':    { name: 'Announcements', type: 'announcement', members: 24 },
};

const INITIAL_MESSAGES = {
  'room-engineering': [
    { id: 'm1', sender: 'Priya Mehta',    text: 'Sprint review at 3pm today', time: '09:15', mine: false },
    { id: 'm2', sender: 'Arjun Patel',    text: 'Will the new API be ready?', time: '09:22', mine: false },
    { id: 'm3', sender: 'You',            text: 'Yes, deploying by 2pm', time: '09:30', mine: true },
    { id: 'm4', sender: 'Priya Mehta',    text: 'Great! See everyone at 3.', time: '09:35', mine: false },
  ],
  'room-general': [
    { id: 'm1', sender: 'Rohan Sharma',  text: 'Good morning everyone!', time: '08:00', mine: false },
    { id: 'm2', sender: 'Sneha Reddy',   text: 'Good morning 👋', time: '08:05', mine: false },
  ],
  'room-announce': [
    { id: 'm1', sender: 'HR Team', text: 'Reminder: Submit your timesheets by Friday EOD.', time: '10:00', mine: false },
    { id: 'm2', sender: 'HR Team', text: 'Office will be closed on June 17 (public holiday).', time: '10:01', mine: false },
  ],
};

export default function ChatRoomPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const user = useSelector(s => s.auth.user);
  const room = MOCK_ROOMS[roomId] || { name: 'Chat', type: 'department', members: 0 };
  const [messages, setMessages] = useState(INITIAL_MESSAGES[roomId] || []);
  const [text, setText] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = () => {
    if (!text.trim()) return;
    setMessages(m => [...m, { id: `m${Date.now()}`, sender: 'You', text: text.trim(), time: format(new Date(), 'HH:mm'), mine: true }]);
    setText('');
  };

  const RoomIcon = room.type === 'announcement' ? Megaphone : room.type === 'direct' ? User : Hash;

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] fade-in">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-[var(--border)]">
        <button onClick={() => navigate('/chat')} className="p-2 hover:bg-[var(--bg-muted)] rounded-[8px] text-[var(--text-secondary)]"><ArrowLeft size={18}/></button>
        <div className="w-9 h-9 rounded-[10px] bg-[var(--accent)]/10 flex items-center justify-center">
          <RoomIcon size={16} className="text-[var(--accent)]"/>
        </div>
        <div>
          <p className="text-[15px] font-bold text-[var(--text-primary)]">{room.name}</p>
          <p className="text-[11px] text-[var(--text-tertiary)]">{room.members} members</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {messages.map(m => (
          <div key={m.id} className={`flex gap-3 ${m.mine ? 'flex-row-reverse' : ''}`}>
            {!m.mine && <Avatar name={m.sender} size="sm"/>}
            <div className={`max-w-[70%] ${m.mine ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}>
              {!m.mine && <p className="text-[11px] font-semibold text-[var(--text-secondary)] px-1">{m.sender}</p>}
              <div className={`px-4 py-2.5 rounded-[16px] text-[13px] ${
                m.mine
                  ? 'bg-[var(--accent)] text-white rounded-tr-[4px]'
                  : 'bg-[var(--bg-muted)] text-[var(--text-primary)] rounded-tl-[4px]'
              }`}>{m.text}</div>
              <p className="text-[10px] text-[var(--text-tertiary)] px-1">{m.time}</p>
            </div>
          </div>
        ))}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      <div className="pt-3 border-t border-[var(--border)]">
        {room.type === 'announcement' ? (
          <p className="text-[12px] text-center text-[var(--text-tertiary)] py-2">Only HR managers can post in this channel</p>
        ) : (
          <div className="flex gap-2">
            <input
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
              placeholder={`Message #${room.name.toLowerCase()}…`}
              className="flex-1 h-11 px-4 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[12px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            />
            <button onClick={send} disabled={!text.trim()}
              className="w-11 h-11 rounded-[12px] bg-[var(--accent)] text-white flex items-center justify-center hover:bg-[var(--accent)]/90 disabled:opacity-40 transition-colors">
              <Send size={16}/>
            </button>
          </div>
        )}
        <p className="text-[10px] text-[var(--text-tertiary)] mt-1.5 text-center">Real-time via Socket.io — Phase 1D</p>
      </div>
    </div>
  );
}
